import { z } from 'zod'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'
import { focalPointSchema } from '../helpers/focal-point.js'
import { safeRedirectPath } from '../helpers/safe-redirect.js'
import { tenantRoleReachSchema } from '../enums/tenant-role-reach.enum.js'
import { storedGrantsSchema } from './tenant-role.dto.js'

/*
 * These are response shapes, so they must tolerate keys they do not know.
 *
 * They were the only strict DTOs in the repo, and on 2026-08-20 that locked
 * people out of staging: the API had started returning `supportVenue`, and every
 * tab still running JS from before that field existed rejected the login
 * response for having an unrecognized key. The session was fine; only the
 * client's idea of the shape was old.
 *
 * Zod's default — strip what you don't recognize — still catches the mistakes
 * worth catching: a field missing, or the wrong type. Rejecting *extra* keys
 * only catches the server being ahead of the client, which is the normal state
 * of the world for a while after every deploy.
 */

/** Present only while a SUPER_ADMIN is in tenant support mode. */
export const authSupportModeDtoSchema = z
  .object({
    tenantId: z.string().uuid(),
    tenantName: z.string().min(1),
  })

export type AuthSupportModeDto = z.infer<typeof authSupportModeDtoSchema>

/**
 * Present only while a session has entered a single venue ("log in to venue").
 * Venue authority is narrowed to this venue for as long as it is set; exiting
 * restores the scope the session had before.
 */
export const authVenueSupportDtoSchema = z
  .object({
    venueId: z.string().uuid(),
    venueName: z.string().min(1),
  })

export type AuthVenueSupportDto = z.infer<typeof authVenueSupportDtoSchema>

/**
 * One venue the session may switch into, for the venue switcher.
 *
 * Deliberately NOT sourced from `GET /venues`: once a session holds an overlay
 * that list is narrowed to the single entered venue, so the switcher would only
 * ever offer the venue you are already in. This is computed from assignments
 * (venue admin) or the tenant (organization admin), ignoring the overlay.
 */
export const switchableVenueDtoSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    /** The venue's cover photograph, signed for the browser; null if none yet. */
    coverImageUrl: browserAssetUrlSchema.nullable(),
    /**
     * Where the subject of that photograph is.
     *
     * Carried here because the "You are working in" band draws this cover as a
     * CSS background, and a band 80 pixels tall crops harder than anything else
     * in the product — it is the surface a focal point matters most for.
     */
    coverFocalPoint: focalPointSchema.nullable(),
    /** The venue this user lands in on sign-in. */
    isPrimary: z.boolean(),
  })

/** Response for `GET /auth/me/venues`. */
export const switchableVenuesDtoSchema = z
  .object({ venues: z.array(switchableVenueDtoSchema) })

export type SwitchableVenuesDto = z.infer<typeof switchableVenuesDtoSchema>

/**
 * What a member of a company's staff may do (docs/custom-roles.spec.md): the
 * name of their role, how far it reaches, and its grants. An organization
 * admin's is fixed: every grant, the whole company. The web app shows only
 * what this allows; the API decides.
 *
 * Grants are read leniently, so an area added after this client was built is
 * dropped and one it lacks reads as none: drift only ever hides a control.
 */
export const authStaffAccessDtoSchema = z.object({
  roleName: z.string().min(1),
  reach: tenantRoleReachSchema,
  grants: storedGrantsSchema,
})
export type AuthStaffAccessDto = z.infer<typeof authStaffAccessDtoSchema>

/**
 * `access` on the wire. Optional only so that a browser running this build
 * against an API from before it — the minutes a deploy takes — still signs in
 * (see the note at the top of this file); every API since sends it.
 */
const staffAccessField = authStaffAccessDtoSchema.optional()

const authUserBaseSchema = {
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  avatarUrl: browserAssetUrlSchema.nullable(),
}

/**
 * Platform SUPER_ADMIN (not inside a tenant). `tenantId` / `support` / `vendorId`
 * are fixed null so illegal combos cannot type-check.
 */
export const authUserSuperAdminPlatformDtoSchema = z
  .object({
    ...authUserBaseSchema,
    role: z.literal('SUPER_ADMIN'),
    tenantId: z.null(),
    vendorId: z.null(),
    support: z.null(),
    supportVenue: z.null(),
  })

/**
 * SUPER_ADMIN in tenant support mode. Effective scope is `tenantId`; `support`
 * carries the banner payload and must match that tenant.
 */
export const authUserSuperAdminSupportDtoSchema = z
  .object({
    ...authUserBaseSchema,
    role: z.literal('SUPER_ADMIN'),
    tenantId: z.string().uuid(),
    vendorId: z.null(),
    support: authSupportModeDtoSchema,
    supportVenue: authVenueSupportDtoSchema.nullable(),
  })
  .refine((u) => u.support.tenantId === u.tenantId, {
    message: 'support.tenantId must equal tenantId',
    path: ['support', 'tenantId'],
  })

/**
 * Organization admin. The only tenant member who can hold a venue overlay: a
 * venue admin is already venue-scoped, and a client has no admin surface.
 */
export const authUserOrganizationAdminDtoSchema = z
  .object({
    ...authUserBaseSchema,
    role: z.literal('ORGANIZATION_ADMIN'),
    tenantId: z.string().uuid(),
    vendorId: z.null(),
    support: z.null(),
    supportVenue: authVenueSupportDtoSchema.nullable(),
    access: staffAccessField,
  })

/**
 * Venue admin. Carries a venue overlay because a venue admin works in one venue
 * at a time: the session lands in their primary venue and the switcher moves it.
 * For them the overlay *selects* among the venues they manage — it can never add
 * one (see VenueAccessService.scopeFor, which intersects with assignments).
 */
export const authUserVenueAdminDtoSchema = z
  .object({
    ...authUserBaseSchema,
    role: z.literal('VENUE_ADMIN'),
    tenantId: z.string().uuid(),
    vendorId: z.null(),
    support: z.null(),
    supportVenue: authVenueSupportDtoSchema.nullable(),
    access: staffAccessField,
  })

/** Tenant client — no admin surface, so never an overlay. */
export const authUserClientDtoSchema = z
  .object({
    ...authUserBaseSchema,
    role: z.literal('CLIENT'),
    tenantId: z.string().uuid(),
    vendorId: z.null(),
    support: z.null(),
    supportVenue: z.null(),
  })

/** Vendor user (outside any tenant). */
export const authUserVendorDtoSchema = z
  .object({
    ...authUserBaseSchema,
    role: z.literal('VENDOR'),
    tenantId: z.null(),
    vendorId: z.string().uuid(),
    support: z.null(),
    supportVenue: z.null(),
  })

/**
 * Authenticated user on the wire. Discriminated by role (+ support for super
 * admins) so illegal tenant/vendor/support combinations cannot parse.
 */
export const authUserDtoSchema = z.union([
  authUserSuperAdminPlatformDtoSchema,
  authUserSuperAdminSupportDtoSchema,
  authUserOrganizationAdminDtoSchema,
  authUserVenueAdminDtoSchema,
  authUserClientDtoSchema,
  authUserVendorDtoSchema,
])

export type AuthUserDto = z.infer<typeof authUserDtoSchema>

/**
 * A full authenticated session: a short-lived access token (sent as the Bearer
 * on every request) plus a long-lived, single-use refresh token (exchanged at
 * `POST /auth/refresh` for a new pair once the access token expires).
 * Returned by both `POST /auth/login` and `POST /auth/refresh`.
 */
export const authSessionDtoSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: authUserDtoSchema,
})

export type AuthSessionDto = z.infer<typeof authSessionDtoSchema>

/**
 * Where to land after signing in, read through `safeRedirectPath` once more.
 * Anything it would not honor arrives as null, and the client lands them where
 * their role normally starts: the app navigates to it, and the router throws on
 * an absolute or protocol-relative target after the session is already stored.
 */
const landingPathSchema = z
  .string()
  .nullable()
  .transform((path) => safeRedirectPath(path))

/**
 * Response from `POST /auth/magic-link`: a session, plus where the holder was
 * trying to go when they were bounced to the login page.
 *
 * `redirectTo` is a path this server vouched for at the moment the link was
 * issued — it never traveled in the URL, so it is not whatever the person who
 * clicked would like it to be. Null when they asked for a link from the login
 * page directly, or when the destination could not be vouched for; the client
 * then lands them wherever their role normally starts.
 */
export const magicLinkSessionDtoSchema = authSessionDtoSchema.extend({
  redirectTo: landingPathSchema,
})

export type MagicLinkSessionDto = z.infer<typeof magicLinkSessionDtoSchema>

/**
 * Response from `POST /auth/accept-invitation`: a session, and where the
 * invitation was meant to land them, such as the proposal a couple was
 * invited to see. Defaulted while an older API is still answering.
 */
export const invitationSessionDtoSchema = authSessionDtoSchema.extend({
  redirectTo: landingPathSchema.default(null),
})

export type InvitationSessionDto = z.infer<typeof invitationSessionDtoSchema>

/** Whose account an invitation or password-reset link is for (`POST /auth/link-account`). */
export const linkAccountDtoSchema = z.object({
  email: z.string().email(),
})

export type LinkAccountDto = z.infer<typeof linkAccountDtoSchema>

/** Response from `POST /auth/login` (an authenticated session). */
export const loginResponseDtoSchema = authSessionDtoSchema

export type LoginResponseDto = AuthSessionDto
